import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { SofaButton, SofaPanel } from '@sofology/sofologycomponents';
import { RouteComponentProps } from 'react-router';

import './FinanceApplicationStepTwoPage.scss';

import { FinanceBackButton, Loader, StepBar } from 'components';
import { scrollHelper, generateDispatchToPropsMap, GtmCheckoutSteps, logsHelper, errorToBasketErrorModel } from 'helpers';
import { preprocessRedirectToNextStep, PreprocessRedirectToNextStepFunc } from 'store/checkout/actions';
import { AppState } from 'store';
import { FinanceContactDetailsForm } from './';
import { FinanceContactDetailsFormType } from './FinanceContactDetailsForm/FinanceContactDetailsForm';
import { saveIfcFinanceData, SaveIfcFinanceDataFunc } from 'store/checkout/actions/finance';
import { routes } from 'routes';
import { IlpQuestionDescriptions, IfcApplicationData } from 'models';

interface OwnState {
    isContinueClickProcessing: boolean;
    isContinueButtonDisabled: boolean;
}

interface StateProps {
    firstFinanceApplicationPageInitialized: boolean;
    ifcQuestionSet: IlpQuestionDescriptions | null;
    ifcApplicationData: IfcApplicationData | null;
}

interface DispatchProps {
    saveIfcFinanceData: SaveIfcFinanceDataFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
}

const CURRENT_STEP = 2;
const STEPS_COUNT = 4;

class FinanceApplicationStepTwoPage extends Component<StateProps & DispatchProps & RouteComponentProps, OwnState> {
    private financeContactDetailsForm = React.createRef<FinanceContactDetailsFormType>();

    constructor(props: StateProps & DispatchProps & RouteComponentProps) {
        super(props);

        this.state = {
            isContinueClickProcessing: false,
            isContinueButtonDisabled: false
        };
    }

    componentDidMount() {
        if (!this.props.firstFinanceApplicationPageInitialized) {
            this.props.history.push(routes.financeApplication.path);
        }
    }

    handleContinueClick = () => {
        this.setState({ isContinueClickProcessing: true }, () => {
            this.validateForm()
                .then(isValid => {
                    if (isValid) {
                        const formComponent = this.financeContactDetailsForm.current!;
                        const values = formComponent.getFormData();

                        this.props.saveIfcFinanceData(values);

                        this.props.preprocessRedirectToNextStep(
                            () => this.props.history.push(routes.financeApplicationStepThree.path),
                            GtmCheckoutSteps.FinanceFormStepThree
                        );
                    } else {
                        this.setState({ isContinueClickProcessing: false });
                    }
                })
                .catch(error => {
                    logsHelper.error(errorToBasketErrorModel(error, 'FinanceApplicationStepTwoPage:handleContinueClick'));
                    this.setState({ isContinueClickProcessing: false });
                });
        });
    };

    handleBackButtonClick = async () => {
        const values = this.financeContactDetailsForm.current!.getFormData();

        this.props.saveIfcFinanceData(values);
        this.props.history.push(routes.financeApplication.path);
    };


    handleGoToStartCheckout = () => {
        this.props.history.push(routes.deliveryDetails.path);
    };

    handleDynamicFormInitialized = () => {
        this.setState({ isContinueButtonDisabled: this.financeContactDetailsForm.current!.hasInvalidReadonlyControls() });
    };

    render(): JSX.Element {
        const { firstFinanceApplicationPageInitialized } = this.props;

        return (
            <div className='finance-application-step-two-page'>
                {
                    firstFinanceApplicationPageInitialized
                        ? this.renderContent()
                        : <Loader show={true} />
                }
            </div>
        );
    }

    renderContent(): JSX.Element {
        return (
            <>
                <FinanceBackButton onClick={this.handleBackButtonClick} />
                <StepBar stepsCount={STEPS_COUNT} currentStep={CURRENT_STEP} />
                <SofaPanel>
                    <FinanceContactDetailsForm
                        ref={this.financeContactDetailsForm}
                        ifcApplicationData={this.props.ifcApplicationData!}
                        ifcQuestionSet={this.props.ifcQuestionSet!}
                        onGoToStartCheckout={this.handleGoToStartCheckout}
                        onDynamicFormInitialized={this.handleDynamicFormInitialized}
                    />
                </SofaPanel>
                {this.renderContinueButton()}
            </>
        );
    }

    renderContinueButton(): JSX.Element {
        return (
            <div className='finance-application-step-two-page__continue-button'>
                {this.state.isContinueClickProcessing && <Loader show={true} overlay={true} />}
                <SofaButton
                    text='Continue'
                    variant='primary'
                    disabled={this.state.isContinueButtonDisabled}
                    onClick={this.handleContinueClick}
                />
            </div>
        );
    }

    async validateForm(): Promise<boolean> {
        let isValid = true;
        const formComponent = this.financeContactDetailsForm.current!;
        const invalidControls = await formComponent.validate();

        if (invalidControls.length > 0) {
            isValid = false;

            let firstInvalidControl = invalidControls.min(c => c.index);
            firstInvalidControl.ref?.current && scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
        }

        return isValid;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        firstFinanceApplicationPageInitialized: state.checkoutState.firstFinanceApplicationPageInitialized,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet,
        ifcApplicationData: state.checkoutState.ifcApplicationData
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [saveIfcFinanceData, preprocessRedirectToNextStep]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(FinanceApplicationStepTwoPage);